import { useCallback } from "react";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { useShallow } from "zustand/react/shallow";
import type { Doc, Id } from "@/../convex/_generated/dataModel";

// Les templates du canvas courant (custom nodes et Databases), syncés depuis
// la route canvas (listForCanvas). Map + sélecteurs granulaires, miroir de
// nodeDataStore : éditer le template T ne re-rend que les nodes qui
// l'utilisent. Ne JAMAIS mettre l'objet template dans les data React Flow
// (casserait areNodePropsEqual).

interface TemplatesStore {
  templates: Map<Id<"nodeTemplates">, Doc<"nodeTemplates">>;
  // Les templates du canvas dans l'ordre du menu d'ajout (par nom, comme le
  // serveur les rend).
  canvasTemplateIds: Id<"nodeTemplates">[];
  // Les templates du canvas : ceux qui n'y sont plus (supprimés) quittent la
  // map, les inchangés (même `updatedAt`) gardent leur référence.
  syncTemplates: (templates: Doc<"nodeTemplates">[]) => void;
  clear: () => void;
}

export const useTemplatesStore = create<TemplatesStore>()(
  devtools(
    (set) => ({
      templates: new Map(),
      canvasTemplateIds: [],

      syncTemplates: (templates) => {
        set((state) => {
          let changed = templates.length !== state.templates.size;
          const next = new Map<Id<"nodeTemplates">, Doc<"nodeTemplates">>();
          for (const template of templates) {
            const existing = state.templates.get(template._id);
            if (existing && existing.updatedAt === template.updatedAt) {
              next.set(template._id, existing);
            } else {
              next.set(template._id, template);
              changed = true;
            }
          }
          const ids = templates.map((template) => template._id);
          const sameIds =
            ids.length === state.canvasTemplateIds.length &&
            ids.every((id, i) => id === state.canvasTemplateIds[i]);
          if (!changed && sameIds) return state;
          return {
            templates: changed ? next : state.templates,
            canvasTemplateIds: sameIds ? state.canvasTemplateIds : ids,
          };
        });
      },

      clear: () => set({ templates: new Map(), canvasTemplateIds: [] }),
    }),
    { name: "templates-store" },
  ),
);

/** Template par id — re-render uniquement quand CE template change. */
export function useTemplate(
  templateId: string | undefined,
): Doc<"nodeTemplates"> | undefined {
  return useTemplatesStore(
    useCallback(
      (state) =>
        templateId
          ? state.templates.get(templateId as Id<"nodeTemplates">)
          : undefined,
      [templateId],
    ),
  );
}

/**
 * Les templates du canvas, prêts au premier rendu du menu d'ajout. La route
 * canvas est déjà abonnée à listForCanvas : le menu lit ce cache au lieu
 * d'ouvrir sa propre subscription, sinon chaque ouverture repartait d'un état
 * de chargement et la liste apparaissait APRÈS que le menu ait été mesuré et
 * positionné (cf. ContextMenuWrapper) — donc hors de l'écran.
 */
export function useCanvasTemplates(): Doc<"nodeTemplates">[] {
  return useTemplatesStore(
    useShallow((state) =>
      state.canvasTemplateIds
        .map((id) => state.templates.get(id))
        .filter((template): template is Doc<"nodeTemplates"> => !!template),
    ),
  );
}
